<template>
  <div>
    <BreadCrumb
      :path="[
        {
          title: 'My Organizations',
          route: '/organizations',
        },
        {
          title: organization?.name,
          route: `/organization/${organizationId}`,
        },
      ]"
    />
    <MainContentPanel title="Organization">
      <div
        class="loader"
        v-if="
          loading || notFound || notAuthorized || unknownError || !organization?.members?.length
        "
      >
        <ResourceLoadStatus
          :loading="loading"
          :not-found="notFound"
          :unauthorized="notAuthorized || !organization?.members?.length"
          :unknownError="unknownError"
        />
      </div>
      <div v-else-if="organization">
        <form class="uk-flex uk-flex-column">
          <form-group class="uk-margin">
            <span class="uk-inline">
              Name:
              <input
                id="user-name"
                class="uk-input"
                type="text"
                v-model="organization.name"
                required
                :disabled="true"
              />
            </span>
          </form-group>
        </form>
        <div style="width: 48%">
          <div>
            <HeadingSecondary
              v-if="adminMode"
              title="Start a Debate"
              :small="true"
              buttonIcon="Create-Discussions"
              buttonText="Create a New Document"
              tooltip="Click Here to Submit a New Document"
              :submenu="[
                {
                  icon: 'link',
                  nav: `/submit-new-article?organizationId=${organizationId}`,
                  headerText: 'Generate from URL',
                },
                {
                  icon: 'pencil',
                  nav: `/submit-new-text?organizationId=${organizationId}`,
                  headerText: 'Write Text Document',
                },
                {
                  icon: 'list',
                  nav: `/submit-new-outline?organizationId=${organizationId}`,
                  headerText: 'Outline a Discussion',
                },
              ]"
            />
          </div>
        </div>

        <div style="display: flex">
          <div style="width: 48%">
            <HeadingSecondary
              v-if="adminMode"
              title="Organization Members"
              :small="true"
              buttonIcon="User"
              buttonText="Add Members"
              tooltip="Click here to invite a user"
              :buttonAction="inviteUser"
            />
            <HeadingSecondary v-else title="Organization Members" :small="true" />
            <div>
              <PrimeDataTable
                ref="membersDataTable"
                :tableConfig="membersTableConfig"
                :tableOptions="membersTableOptions"
                :page-size="pageSize"
                :loadDataFunction="loadOrganizationMembers"
                @selectedColumns="tableToggleData"
              />
            </div>
          </div>
          <div style="flex-grow: 1"></div>
          <div style="width: 48%">
            <HeadingSecondary
              v-if="adminMode"
              title="Organization Teams"
              :small="true"
              buttonIcon="referee"
              tooltip="Click here to create a team"
              :buttonAction="createTeam"
            />
            <HeadingSecondary v-else title="Organization Teams" :small="true" />
            <div>
              <PrimeDataTable
                ref="teamsDataTable"
                :tableConfig="teamsTableConfig"
                :tableOptions="teamsTableOptions"
                :page-size="pageSize"
                :loadDataFunction="loadOrganizationTeams"
                @selectedColumns="tableToggleData"
              />
            </div>
          </div>
        </div>
      </div>

      <div ref="organizationMemberInvitationModal" uk-modal>
        <BasicModal>
          <OrganizationMemberInvitation
            ref="organizationMemberInvitation"
            @submit="inviteUserSubmit"
          />
        </BasicModal>
      </div>
    </MainContentPanel>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import MainContentPanel from '@/components/display/general/MainContentPanel';
import HeadingSecondary from '@/components/general/HeadingSecondary';
import ResourceLoadStatus from '@/components/general/ResourceLoadStatus';
import BasicModal from '@/components/display/general/BasicModal';
import OrganizationMemberInvitation from '@/components/organization/OrganizationMemberInvitation';
import BreadCrumb from '@/components/general/BreadCrumb';
import { mapActions, mapGetters, mapState } from 'vuex';
import UIkit from 'uikit';
import { xhr } from '@/xhr/endpoints';

export default {
  name: 'OrganizationDetails',
  components: {
    MainContentPanel,
    HeadingSecondary,
    ResourceLoadStatus,
    PrimeDataTable: defineAsyncComponent(() => import('@/components/table/PrimeDataTable.vue')),
    BasicModal,
    OrganizationMemberInvitation,
    BreadCrumb,
  },
  data() {
    return {
      notAuthorized: false,
      notFound: false,
      unknownError: false,
      pageSize: 10,
    };
  },
  computed: {
    ...mapState(['userData', 'idToken', 'organizationDetailsCache']),
    ...mapGetters(['tableToggleData']),
    organizationId() {
      return this.$route.params.id;
    },
    organization() {
      return this.organizationDetailsCache[this.organizationId];
    },
    loading() {
      return !this.organizationDetailsCache[this.organizationId];
    },
    adminMode() {
      return (
        this.userData &&
        this.organization &&
        this.organization.members.find(
          x => x.role === 'Admin' && x.user && x.user._id === this.userData.id
        )
      );
    },
    membersTableConfig() {
      const membersTableConfig = [
        {
          column: 'Name',
          field: 'name',
          model: row =>
            row.user ? row.user.name : `${row.invitation.name} (${row.invitation.status})`,
          sortable: true,
          index: 1,
        },
        {
          column: 'Org. Role',
          field: 'role',
          model: row => row.role,
          sortable: true,
          isDropDown: true,
          dropDownOption: ['Admin', 'Member'],
          filter: {
            type: 'select',
            options: ['Admin', 'Member'],
          },
          index: 2,
        },
      ];
      return membersTableConfig;
    },
    membersTableOptions() {
      let options = { globalFilterFields: ['name'], tableName: 'tableOrgMembers' };
      if (this.adminMode)
        options.deleteSelected = {
          action: this.removeMultipleMembers,
          title: 'Deleting Organization Members',
        };
      return options;
    },

    teamsTableConfig() {
      const teamsTableConfig = [
        {
          column: 'Name',
          field: 'name',
          model: row => row.name,
          link: row => `/organization-team/${row._id}?organizationId=${this.organizationId}`,
          sortable: true,
          index: 1,
        },
      ];
      return teamsTableConfig;
    },
    teamsTableOptions() {
      let options = { tableName: 'tableOrgTeams' };
      if (this.adminMode)
        options.deleteSelected = {
          action: this.removeMultipleTeams,
          title: 'Deleting Organization Teams',
        };
      return options;
    },
  },
  async mounted() {
    this.loadOrganizationDetails(this.organizationId);
  },
  methods: {
    ...mapActions(['loadOrganizationDetails', 'loadOrganizationMembers', 'loadOrganizationTeams']),

    createTeam() {
      this.$router.push(`/organization-team/create?organizationId=${this.organizationId}`);
    },

    inviteUser() {
      UIkit.modal(this.$refs.organizationMemberInvitationModal).show();
    },
    async inviteUserSubmit(data) {
      this.$refs.organizationMemberInvitation.reset();
      UIkit.modal(this.$refs.organizationMemberInvitationModal).hide();
      const updatedOrg = await xhr.inviteOrganizationMembers(
        this.organizationId,
        data.map(x => ({ ...x, message: 'Please join my organization!' })),
        this.idToken
      );
      if (updatedOrg) {
        this.$refs.membersDataTable.loadData();
      }
      console.log(data);
    },

    async removeMultipleMembers({ items, success, failed }) {
      try {
        const itemsIds = items.map(item => item._id);
        await xhr.removeOrganizationMembers(this.organizationId, itemsIds, this.idToken);
        if (success) success();
        this.$refs.membersDataTable.loadData();
      } catch (e) {
        console.log(e);
        if (failed) failed();
      }
    },
    async removeMultipleTeams({ items, success, failed }) {
      try {
        const itemsIds = items.map(item => item._id);
        await xhr.removeOrganizationTeams(this.organizationId, itemsIds, this.idToken);
        if (success) success();
        this.$refs.teamsDataTable.loadData();
      } catch (e) {
        console.log(e);
        if (failed) failed();
      }
    },
  },
};
</script>

<style lang="sass" scoped>
.loader
  position: absolute
  width: 100%
  height: 100%
</style>
